import { useMemo } from 'react';
import { extractUtmParamsFromWindow, mergeQueryParams, isUtmRestrictedPath } from 'helpers/urlParams';

/**
 * Hook to extract and normalize query parameters from CMS contactLink
 * Returns both the query parameter variants for validation and the extracted query string
 * Also provides utilities for building URLs that preserve UTM parameters
 */
export function useContactLinkQuery(contactLink?: { contactLink: Umbraco.Link }) {
	const queryParam = useMemo(() => {
		if (!contactLink?.contactLink?.url) return 'contact=show';

		const urlString = contactLink.contactLink.url;
		const queryStart = urlString.indexOf('?');
		if (queryStart === -1) return 'contact=show';

		const hashStart = urlString.indexOf('#', queryStart);
		const query = hashStart === -1 ? urlString.substring(queryStart + 1) : urlString.substring(queryStart + 1, hashStart);

		return query || 'contact=show';
	}, [contactLink]);

	// Generate case-insensitive variants for validation
	const validQueryParams = useMemo(() => {
		const variants = [queryParam];
		const lowerCase = queryParam.toLowerCase();
		if (lowerCase !== queryParam) variants.push(lowerCase);

		// Also add capitalized version (Contact=show)
		const capitalized = queryParam.replace(/^(\w)/, (match) => match.toUpperCase());
		if (capitalized !== queryParam && capitalized !== lowerCase) {
			variants.push(capitalized);
		}

		return variants;
	}, [queryParam]);

	/**
	 * Build a URL with query parameters, preserving UTM parameters from current URL
	 * @param includeContact - Whether to include the contact query parameter
	 * @param additionalParams - Additional query parameters to include
	 * @returns Query string (without leading '?')
	 */
	const buildUrlWithParams = useMemo(() => {
		return (includeContact: boolean = true, additionalParams: Record<string, string> = {}): string => {
			// Extract UTM parameters from current URL, except in account/checkout flows
			const preserveUtm = typeof window !== 'undefined' && !isUtmRestrictedPath(window.location.pathname);
			const utmParams = preserveUtm ? extractUtmParamsFromWindow() : {};

			// Parse the contact query parameter (e.g., "contact=show" -> { contact: "show" })
			const contactParams: Record<string, string> = {};
			if (includeContact) {
				const [key, value] = queryParam.split('=');
				if (key && value) {
					contactParams[key] = value;
				}
			}

			// Ensures URL pattern is: ?Contact=show&utm_source=...&utm_medium=...
			const merged = mergeQueryParams(contactParams, utmParams, additionalParams);
			return merged.toString();
		};
	}, [queryParam]);

	return {
		/** The primary query parameter extracted from CMS (e.g., "contact=show") */
		queryParam,
		/** Array of case variants for validation (e.g., ["contact=show", "Contact=show"]) */
		validQueryParams,
		/** Helper function to build URLs with preserved UTM parameters */
		buildUrlWithParams,
	};
}
